import { Typography, useMobile } from '@sonnen/web-ui';
import { ReactNode } from 'react';
import {
  CartesianGrid,
  DotProps,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { CategoricalChartFunc } from 'recharts/types/chart/types';
import { QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED } from '../../../constants';
import { useTranslation } from '../../../i18n/i18n';
import { useOfferContext } from '../../../OfferContext/OfferContext';
import { BlueDot } from './BlueDot';
import {
  getCurrentDataIndex,
  getIntervalPeriod,
  getTicks,
  getTickValues,
} from './QuarterlyPriceChart.helpers';
import styles from './QuarterlyPriceChart.module.css';
const CHART_HEIGHT = 300;

export const QuarterlyPriceChart = () => {
  const { t } = useTranslation();
  const { quarterlyPriceData } = useOfferContext();
  const mobile = useMobile();

  const defaultIndex: number = quarterlyPriceData ? getCurrentDataIndex(quarterlyPriceData) : 0;

  const getTooltipContent = (props: TooltipContentProps<string, string>): ReactNode => {
    const { payload } = props || {};

    if (!payload?.[0]?.payload) return null;

    const timestamp = payload?.[0].payload.timestamp;

    return (
      <div className={styles.tooltipContent} data-testid="tooltip-content">
        <Typography.Label2 variant="on-colour">{getIntervalPeriod(timestamp)}</Typography.Label2>
      </div>
    );
  };

  const { yTicks, domain, xTicks } = getTicks(quarterlyPriceData || []);
  const { centPriceData, pastData, futureData } = quarterlyPriceData
    ? getTickValues(quarterlyPriceData, defaultIndex)
    : {};

  const onMouseMove: CategoricalChartFunc = (e) => {
    if (!e?.activeLabel || !centPriceData) return;
    const hoveredIndex = parseInt(e.activeLabel);
    if (!isNaN(hoveredIndex) && centPriceData[hoveredIndex]) {
      window.dispatchEvent(
        new CustomEvent(QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED, { detail: hoveredIndex }),
      );
    }
  };

  const tickHeight = CHART_HEIGHT / yTicks.length;
  const horizontalPoints = yTicks.map((_tick, index) => index * tickHeight + 50);
  horizontalPoints.push(50 + tickHeight * yTicks.length);

  if (!quarterlyPriceData) return null;

  return (
    <div data-testid="quarterly-price-chart">
      <ResponsiveContainer width="100%" height={380}>
        <LineChart
          onMouseMove={onMouseMove}
          onTouchMove={onMouseMove}
          onClick={mobile ? onMouseMove : undefined}
          width={630}
          height={340}
          margin={{ top: 35, right: 10, left: mobile ? 10 : 5 }}
          data={centPriceData}
        >
          <defs>
            <linearGradient id="lineColor" gradientTransform="rotate(90)">
              <stop offset="0.195435" stopColor="#FB7835" />
              <stop offset="0.8" stopColor="#0040FA" />
            </linearGradient>
          </defs>

          {/* Future data line - dashed */}
          <Line
            type="stepAfter"
            xAxisId="futureChart"
            yAxisId="futureChart"
            dataKey="value"
            stroke="url(#lineColor)"
            strokeWidth={2}
            strokeDasharray="5 5"
            fill="transparent"
            dot={false}
            activeDot={(props: DotProps) => <BlueDot cx={props.cx} cy={props.cy} />}
            data={futureData}
            opacity={0.3}
          />

          {/* Past data line - solid */}
          <Line
            type="stepAfter"
            xAxisId="pastChart"
            yAxisId="pastChart"
            dataKey="value"
            stroke="url(#lineColor)"
            strokeWidth={2}
            fill="transparent"
            dot={false}
            activeDot={(props: DotProps) => <BlueDot cx={props.cx} cy={props.cy} />}
            data={pastData}
            opacity={0.8}
          />

          <CartesianGrid vertical={false} x={0} horizontalPoints={horizontalPoints} width={1000} />
          <XAxis strokeWidth={0} allowDataOverflow xAxisId="futureChart" hide />
          <YAxis allowDataOverflow yAxisId="futureChart" ticks={yTicks} hide domain={domain} />
          <XAxis
            xAxisId="pastChart"
            strokeWidth={0}
            dataKey="timestamp"
            tick={{ fontSize: 11, fillOpacity: 0.5, dy: -30 }}
            tickLine={false}
            ticks={xTicks}
            tickFormatter={(timestamp) => {
              const date = new Date(timestamp);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes();
              return minutes === 0
                ? `${hours}:00`
                : `${hours}:${minutes.toString().padStart(2, '0')}`;
            }}
          />
          <YAxis
            width={0.1}
            allowDataOverflow
            yAxisId="pastChart"
            strokeWidth={0}
            type="number"
            ticks={yTicks}
            tick={{ fill: '#0A1535', opacity: 0.32, fontSize: 10, dx: 100, dy: 10 }}
            domain={domain}
          >
            <Label
              value={t('centPerKwh')}
              position="top"
              fillOpacity={0.5}
              fontSize={10}
              dx={100}
            />
          </YAxis>

          <Tooltip
            cursor={{
              stroke: '#000',
              strokeWidth: 2,
            }}
            isAnimationActive={false}
            active
            defaultIndex={defaultIndex}
            content={getTooltipContent}
            offset={-58}
            position={{ y: 30 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
